import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CalendarClock, Trash2, Mail, Users, Eye, Search, Check } from "lucide-react";
import { EMAIL_TEMPLATES } from "@/lib/emailTemplates";
import { FLYER_DESIGNS, renderFlyer } from "@/lib/emailFlyers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = { all: "All leads", new: "New", contacted: "Contacted", proposal_sent: "Proposal Sent", won: "Won", lost: "Lost" };

export default function CampaignsPanel({ token, onExpire }) {
  const [audience, setAudience] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all");
  const [recipMode, setRecipMode] = useState("segment"); // segment | pick
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]); // hand-picked lead ids
  const [leadQuery, setLeadQuery] = useState("");
  const [mode, setMode] = useState("now"); // now | schedule
  const [when, setWhen] = useState("");
  const [design, setDesign] = useState("teal");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c, l] = await Promise.all([
        axios.get(`${API}/admin/audience`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAudience(a.data);
      setCampaigns(c.data);
      setLeads(l.data);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load campaigns.");
    } finally { setLoading(false); }
  }, [token, onExpire]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message."); return; }
    if (mode === "schedule" && !when) { toast.error("Pick a date & time."); return; }
    if (recipMode === "pick" && selected.length === 0) { toast.error("Select at least one customer."); return; }
    setSending(true);
    try {
      const body = { subject, message, design, send_now: mode === "now" };
      if (recipMode === "pick") body.recipient_ids = selected;
      else body.target = target;
      if (mode === "schedule") body.scheduled_at = new Date(when).toISOString();
      const { data } = await axios.post(`${API}/admin/campaigns`, body, { headers });
      toast.success(mode === "now" ? "Campaign is sending now." : "Campaign scheduled.");
      setSubject(""); setMessage(""); setWhen("");
      load();
      return data;
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error(e.response?.data?.detail || "Failed to create campaign.");
    } finally { setSending(false); }
  };

  const cancel = async (id) => {
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`, { headers });
      toast.success("Campaign cancelled.");
      load();
    } catch { toast.error("Failed to cancel."); }
  };

  const segCount = target === "all" ? audience.all : audience[target];
  const count = recipMode === "pick" ? selected.length : segCount;
  const emailLeads = leads.filter((l) => l.email);
  const filteredLeads = emailLeads.filter((l) => {
    const q = leadQuery.trim().toLowerCase();
    return !q || [l.name, l.business, l.email, l.phone].some((v) => (v || "").toLowerCase().includes(q));
  });
  const allFilteredSelected = filteredLeads.length > 0 && filteredLeads.every((l) => selected.includes(l.id));
  const toggleLead = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAllFiltered = () => {
    if (allFilteredSelected) setSelected((p) => p.filter((id) => !filteredLeads.some((l) => l.id === id)));
    else setSelected((p) => [...new Set([...p, ...filteredLeads.map((l) => l.id)])]);
  };
  const inputCls = "w-full px-4 py-3 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors";

  if (loading && !campaigns.length && !Object.keys(audience).length) return <div className="grid place-items-center py-20 text-slate-500"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="campaigns-panel">
      {/* Composer */}
      <form onSubmit={submit} className="bg-white border border-[#E7E5DF] rounded-2xl p-6 shadow-soft space-y-4 h-fit">
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B0F14]"><Mail size={18} className="text-emerald-600" /> New Email Campaign</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Start from a template</label>
          <select className={inputCls} data-testid="campaign-template"
            onChange={(e) => { const t = EMAIL_TEMPLATES.find((x) => x.id === e.target.value); if (t) { setSubject(t.subject); setMessage(t.message); if (t.design) setDesign(t.design); } }}
            defaultValue="">
            <option value="">— Choose a proven template (optional) —</option>
            {EMAIL_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Flyer design</label>
          <div className="flex flex-wrap gap-2" data-testid="campaign-design-picker">
            {FLYER_DESIGNS.map((d) => (
              <button type="button" key={d.id} onClick={() => setDesign(d.id)} data-testid={`campaign-design-${d.id}`}
                className={`rounded-xl border-2 px-2.5 py-2 text-left transition-all ${design === d.id ? "border-emerald-600 bg-emerald-50/40" : "border-[#E7E5DF] hover:border-slate-300"}`}
                title={d.name}>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full" style={{ background: d.hero }} />
                  <span className="w-4 h-4 rounded-full" style={{ background: d.accent }} />
                  <span className="w-4 h-4 rounded-full" style={{ background: d.chip }} />
                </span>
                <span className="block mt-1 text-[10px] font-semibold text-slate-600">{d.name}</span>
              </button>
            ))}
          </div>
        </div>
        <input className={inputCls} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" />
        <textarea className={`${inputCls} resize-none`} rows={6} placeholder="Write your message… (plain text, line breaks preserved)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="campaign-message" />
        <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Recipients</label>
          <div className="flex rounded-xl border border-[#E7E5DF] overflow-hidden mb-2">
            <button type="button" onClick={() => setRecipMode("segment")} data-testid="recip-mode-segment"
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${recipMode === "segment" ? "bg-[#0E3B3B] text-white" : "bg-white text-[#0B0F14]"}`}>By segment</button>
            <button type="button" onClick={() => setRecipMode("pick")} data-testid="recip-mode-pick"
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${recipMode === "pick" ? "bg-[#0E3B3B] text-white" : "bg-white text-[#0B0F14]"}`}>Pick customers</button>
          </div>

          {recipMode === "segment" ? (
            <select className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="campaign-target">
              {Object.keys(STAGE_LABELS).map((k) => (
                <option key={k} value={k}>{STAGE_LABELS[k]}{audience[k] != null ? ` (${audience[k]})` : ""}</option>
              ))}
            </select>
          ) : (
            <div className="rounded-xl border border-[#E7E5DF] bg-white overflow-hidden" data-testid="recip-picker">
              <div className="flex items-center gap-2 p-2 border-b border-[#E7E5DF]">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={leadQuery} onChange={(e) => setLeadQuery(e.target.value)} placeholder="Search customers…" data-testid="recip-search"
                    className="w-full pl-8 pr-2 py-2 rounded-lg bg-[#FBFAF8] border border-[#E7E5DF] text-sm focus:outline-none focus:border-emerald-600/60" />
                </div>
                <button type="button" onClick={toggleAllFiltered} data-testid="recip-select-all"
                  className="shrink-0 px-2.5 py-2 rounded-lg text-xs font-semibold border border-[#E7E5DF] text-slate-600 hover:border-emerald-500">
                  {allFilteredSelected ? "Clear" : "Select all"}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-[#F0EFEA]">
                {filteredLeads.length === 0 ? (
                  <p className="p-4 text-center text-xs text-slate-400">{emailLeads.length === 0 ? "No customers with an email address yet." : "No matches."}</p>
                ) : filteredLeads.map((l) => {
                  const on = selected.includes(l.id);
                  return (
                    <button type="button" key={l.id} onClick={() => toggleLead(l.id)} data-testid={`recip-item-${l.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${on ? "bg-emerald-50" : "hover:bg-[#FBFAF8]"}`}>
                      <span className={`grid place-items-center w-5 h-5 rounded-md border-2 shrink-0 ${on ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"}`}>
                        {on && <Check size={13} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[#0B0F14] truncate">{l.name}{l.business ? ` · ${l.business}` : ""}</span>
                        <span className="block text-[11px] text-slate-400 truncate">{l.email}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">When</label>
          <div className="flex rounded-xl border border-[#E7E5DF] overflow-hidden">
            <button type="button" onClick={() => setMode("now")} data-testid="campaign-mode-now"
              className={`flex-1 py-3 text-sm font-semibold transition-colors ${mode === "now" ? "bg-[#0E3B3B] text-white" : "bg-white text-[#0B0F14]"}`}>Send now</button>
            <button type="button" onClick={() => setMode("schedule")} data-testid="campaign-mode-schedule"
              className={`flex-1 py-3 text-sm font-semibold transition-colors ${mode === "schedule" ? "bg-[#0E3B3B] text-white" : "bg-white text-[#0B0F14]"}`}>Schedule</button>
          </div>
        </div>
        </div>
        {mode === "schedule" && (
          <input type="datetime-local" className={inputCls} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="campaign-datetime" />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className="flex items-center gap-1.5 text-slate-500 text-sm"><Users size={14} /> {count ?? 0} recipient{count === 1 ? "" : "s"}</p>
          <button type="submit" disabled={sending} data-testid="campaign-submit"
            className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={16} className="animate-spin" /> : mode === "now" ? <Send size={16} /> : <CalendarClock size={16} />}
            {mode === "now" ? "Send Campaign" : "Schedule Campaign"}
          </button>
        </div>
      </form>

      {/* Right column: live preview + campaign list */}
      <div className="space-y-4">
      <div className="bg-white border border-[#E7E5DF] rounded-2xl p-3 shadow-soft" data-testid="campaign-preview">
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-slate-400"><Eye size={13} /> Live preview</p>
          <span className="text-[10px] font-semibold text-slate-400">{FLYER_DESIGNS.find((d) => d.id === design)?.name}</span>
        </div>
        <iframe title="Email preview" data-testid="campaign-preview-frame" srcDoc={renderFlyer(design, subject, message)}
          className="w-full rounded-xl border border-[#eef2f7] bg-white" style={{ height: 520 }} />
      </div>
      {/* Campaign list */}
      <div className="space-y-3" data-testid="campaign-list">
        <h3 className="font-display font-bold text-[#0B0F14]">Campaigns</h3>
        {campaigns.length === 0 && <div className="bg-white border border-[#E7E5DF] rounded-2xl p-8 text-center text-slate-500 text-sm">No campaigns yet.</div>}
        {campaigns.map((c) => (
          <div key={c.id} className="bg-white border border-[#E7E5DF] rounded-2xl p-4" data-testid="campaign-item">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#0B0F14] truncate">{c.subject}</p>
                <p className="text-slate-500 text-xs mt-0.5">To: {STAGE_LABELS[c.target] || c.target}</p>
              </div>
              <span className={`shrink-0 px-2.5 py-1 rounded-full font-mono-tech text-[10px] uppercase tracking-wider ${c.status === "sent" ? "bg-emerald-600/10 text-emerald-700" : c.status === "cancelled" ? "bg-slate-200 text-slate-500" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-400">
                {c.status === "sent" ? `Sent to ${c.recipients_count ?? 0} · ${c.sent_at ? new Date(c.sent_at).toLocaleString() : ""}`
                  : c.status === "scheduled" ? `Scheduled ${c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : ""}` : "Cancelled"}
              </p>
              {c.status === "scheduled" && (
                <button onClick={() => cancel(c.id)} data-testid={`campaign-cancel-${c.id}`} className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 text-xs font-semibold">
                  <Trash2 size={13} /> Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
